import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';

// Auto-refresh every 30 seconds
const REFRESH_INTERVAL = 30000;

function healthColor(h) {
  return h === 'healthy' ? '#22c55e' : h === 'warning' ? '#f59e0b' : h === 'offline' ? '#ef4444' : '#64748b';
}
function healthBg(h) {
  return h === 'healthy' ? '#14532d' : h === 'warning' ? '#422006' : h === 'offline' ? '#450a0a' : '#1e2235';
}
function healthLabel(h) {
  return h === 'healthy' ? '● Connected' : h === 'warning' ? '● Warning' : h === 'offline' ? '● Offline' : '○ Never';
}

function formatTime(min) {
  if (min === null) return 'Never connected';
  if (min < 1)  return 'Just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h < 24 ? `${h}h ${m}m ago` : `${Math.floor(h/24)}d ago`;
}

export default function AgentConnections() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [autoRefresh, setAuto]  = useState(true);
  const [lastRefresh, setLast]  = useState(null);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [serverIPs, setServerIPs] = useState([]);
  const timerRef                = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.getConnectionStatus();
      setData(d);
      setLast(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // Load server IPs once from diagnostics
  useEffect(() => {
    api.getDiagnostics().then(({ data: d }) => {
      const ips = (d?.network?.interfaces || [])
        .filter(i => !i.internal)
        .map(i => i.ip);
      setServerIPs(ips);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(load, REFRESH_INTERVAL);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, load]);

  const devices = data?.devices || [];
  const summary = data?.summary || {};

  const filtered = devices.filter(d => {
    const matchFilter = filter === 'all' || d.connectionHealth === filter;
    const matchSearch = !search || d.hostname.toLowerCase().includes(search.toLowerCase())
      || (d.ipAddress || '').includes(search);
    return matchFilter && matchSearch;
  });

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Endpoint Agent Connection</div>
          <div className="page-subtitle">
            Real-time connection status — server: {serverIPs.length > 0 ? serverIPs.map(ip => `${ip}:3030`).join(' / ') : 'localhost:3030'}
          </div>
        </div>
        <div className="flex-gap">
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : ''}
          </span>
          <button
            className={`btn btn-sm ${autoRefresh ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setAuto(a => !a)}
          >
            {autoRefresh ? '⟳ Auto ON' : '⟳ Auto OFF'}
          </button>
          <button className="refresh-btn" onClick={load}>↻ Refresh Now</button>
        </div>
      </div>

      {loading && <div className="loading">Loading endpoint connections...</div>}

      {!loading && (
        <>
          {/* Summary cards */}
          <div className="cards-row">
            <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setFilter('all')}>
              <div className="card-label">Total Endpoints</div>
              <div className="card-value">{summary.total || 0}</div>
              <div className="card-sub">{filter==='all' ? '▼ showing all' : 'registered'}</div>
            </div>
            <div className="card green" style={{ cursor:'pointer', outline: filter==='healthy'?'2px solid #16a34a':undefined }} onClick={() => setFilter(filter==='healthy'?'all':'healthy')}>
              <div className="card-label">Connected</div>
              <div className="card-value">{summary.online || 0}</div>
              <div className="card-sub">{filter==='healthy' ? '▼ filtered' : 'heartbeat < 5 min'}</div>
            </div>
            <div className="card yellow" style={{ cursor:'pointer', outline: filter==='warning'?'2px solid #d97706':undefined }} onClick={() => setFilter(filter==='warning'?'all':'warning')}>
              <div className="card-label">Warning</div>
              <div className="card-value">{summary.warning || 0}</div>
              <div className="card-sub">{filter==='warning' ? '▼ filtered' : 'heartbeat 5–10 min'}</div>
            </div>
            <div className="card red" style={{ cursor:'pointer', outline: filter==='offline'?'2px solid #dc2626':undefined }} onClick={() => setFilter(filter==='offline'?'all':'offline')}>
              <div className="card-label">Offline</div>
              <div className="card-value">{summary.offline || 0}</div>
              <div className="card-sub">{filter==='offline' ? '▼ filtered' : 'heartbeat > 10 min'}</div>
            </div>
            <div className="card" style={{ cursor:'pointer', outline: filter==='never'?'2px solid #94a3b8':undefined }} onClick={() => setFilter(filter==='never'?'all':'never')}>
              <div className="card-label">Never Seen</div>
              <div className="card-value" style={{ color: '#64748b' }}>{summary.never || 0}</div>
              <div className="card-sub">{filter==='never' ? '▼ filtered' : 'no heartbeat yet'}</div>
            </div>
          </div>

          {/* Connection health bar */}
          {summary.total > 0 && (
            <div className="table-wrap section-gap" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Fleet Connection Health</span>
                <span style={{ fontSize: 13, color: '#22c55e' }}>
                  {Math.round(((summary.online || 0) / summary.total) * 100)}% connected
                </span>
              </div>
              {/* Stacked bar */}
              <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
                {summary.online  > 0 && <div style={{ flex: summary.online,  background: '#22c55e', borderRadius: 4 }} title={`${summary.online} connected`} />}
                {summary.warning > 0 && <div style={{ flex: summary.warning, background: '#f59e0b', borderRadius: 4 }} title={`${summary.warning} warning`} />}
                {summary.offline > 0 && <div style={{ flex: summary.offline, background: '#ef4444', borderRadius: 4 }} title={`${summary.offline} offline`} />}
                {summary.never   > 0 && <div style={{ flex: summary.never,   background: '#334155', borderRadius: 4 }} title={`${summary.never} never seen`} />}
              </div>
              <div className="flex-gap" style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                <span style={{ color: '#22c55e' }}>● Connected ({summary.online})</span>
                <span style={{ color: '#f59e0b' }}>● Warning ({summary.warning})</span>
                <span style={{ color: '#ef4444' }}>● Offline ({summary.offline})</span>
                <span style={{ color: '#64748b' }}>○ Never ({summary.never})</span>
              </div>
            </div>
          )}

          {/* Filter + Search */}
          <div className="flex-gap" style={{ marginBottom: 16 }}>
            {['all','healthy','warning','offline','never'].map(f => (
              <button
                key={f}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? `All (${summary.total||0})`
                  : f === 'healthy' ? `● Connected (${summary.online||0})`
                  : f === 'warning' ? `● Warning (${summary.warning||0})`
                  : f === 'offline' ? `● Offline (${summary.offline||0})`
                  : `○ Never (${summary.never||0})`}
              </button>
            ))}
            <input
              className="form-input"
              style={{ width: 200, padding: '5px 10px', fontSize: 12, marginLeft: 'auto' }}
              placeholder="Search hostname / IP..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Device table */}
          <div className="table-wrap">
            <div className="table-header">
              <div className="table-title">Agent Status ({filtered.length})</div>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Auto-refreshes every 30s
              </span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Hostname</th>
                  <th>OS</th>
                  <th>IP Address</th>
                  <th>Last Heartbeat</th>
                  <th>Connection</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="empty">No agents found.</td></tr>
                )}
                {filtered.map(d => (
                  <tr key={d.id}>
                    <td>
                      <span
                        className="badge"
                        style={{ background: healthBg(d.connectionHealth), color: healthColor(d.connectionHealth) }}
                      >
                        {healthLabel(d.connectionHealth)}
                      </span>
                    </td>
                    <td><strong>{d.hostname}</strong></td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{d.os} {d.osVersion ? `(${d.osVersion})` : ''}</td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#94a3b8' }}>{d.ipAddress || '—'}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{formatTime(d.minutesSinceHeartbeat)}</div>
                      {d.lastHeartbeat && (
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {new Date(d.lastHeartbeat).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td>
                      {/* Mini heartbeat indicator */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: healthColor(d.connectionHealth),
                          boxShadow: d.connectionHealth === 'healthy'
                            ? `0 0 6px ${healthColor(d.connectionHealth)}`
                            : 'none',
                          animation: d.connectionHealth === 'healthy' ? 'pulse 2s infinite' : 'none'
                        }} />
                        <span style={{ fontSize: 12, color: healthColor(d.connectionHealth) }}>
                          {d.connectionHealth === 'healthy' ? 'Active'
                            : d.connectionHealth === 'warning' ? 'Slow'
                            : d.connectionHealth === 'offline' ? 'Lost'
                            : 'Never'}
                        </span>
                      </div>
                    </td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>
                      {d.registeredAt ? new Date(d.registeredAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Server info */}
          <div className="table-wrap" style={{ padding: 20 }}>
            <div className="table-title" style={{ marginBottom: 12 }}>Server Endpoints</div>
            <table>
              <thead>
                <tr><th>Role</th><th>IP Address</th><th>Port</th><th>Protocol</th></tr>
              </thead>
              <tbody>
                {serverIPs.length === 0 && (
                  <tr><td colSpan={4} className="empty">Loading server IPs...</td></tr>
                )}
                {serverIPs.map((ip, i) => (
                  <tr key={ip}>
                    <td><span className="badge success">● {i === 0 ? 'Primary' : `Interface ${i + 1}`}</span></td>
                    <td style={{ fontFamily: 'monospace' }}>{ip}</td>
                    <td>3030</td>
                    <td>HTTP (use HTTPS in production)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
